import React, { useState } from 'react'
import Button from '@material-ui/core/Button'

import { Editing, LoadingButton, StepHint } from '../../../../../components'
import { Main } from '../../../../../components/Layout'
import { Buttons } from '../../../../../components/Steps'
import { energy as energyUtils, validators } from '../../../../../utils'
import { appService } from '../../../../../services'

import EnergySection from './elements/EnergySection'
import Form from './elements/Form'
import Search from './elements/Search'

const ENERGY_KEYS = [
  'servedByElectricity',
  'electricalPowerOperatorId',
  'consumerUnitNumber',
  'electricityInstallationCode',
]

const Address = ({ data, errors, persist, prev, validating }) => {
  // Na edição, o cityZipcode de uma inscrição com Endereço Extra é o da
  // cidade: não deve levar o formulário ao fluxo de CEP único.
  const [address, setAddress] = useState(() =>
    data.weakStreetId ? { ...data, cityZipcode: undefined } : { ...data }
  )
  const [loading, setLoading] = useState(false)
  const [localErrors, setLocalErrors] = useState({})

  const energyBackendErrors = Object.fromEntries(
    Object.entries(errors).filter(([k]) => ENERGY_KEYS.includes(k))
  )

  // Remove erros de campos que o usuário já preencheu após o último submit
  const filteredErrors = (() => {
    const e = { ...errors }
    if (address.cityId) delete e.cityId
    if (address.number || address.withoutNumber) delete e.number
    if (address.street) delete e.streetStr
    if (address.neighborhood) delete e.neighborhood
    if (address.zipcode) delete e.zipcode
    if (`${address.weakStreetId}` !== `${data.weakStreetId}`) {
      delete e.weakStreetId
    }
    return e
  })()

  const update = attr => {
    setAddress(prev => ({ ...prev, ...attr }))

    setLocalErrors(prev => {
      if (Object.keys(prev).length === 0) return prev
      const next = { ...prev }
      Object.keys(attr).forEach(k => delete next[k])
      if ('servedByElectricity' in attr && !attr.servedByElectricity) {
        delete next.electricalPowerOperatorId
        delete next.consumerUnitNumber
        delete next.electricityInstallationCode
      }
      if (
        ('consumerUnitNumber' in attr && attr.consumerUnitNumber) ||
        ('electricityInstallationCode' in attr && attr.electricityInstallationCode)
      ) {
        delete next.consumerUnitNumber
        delete next.electricityInstallationCode
      }
      return next
    })
  }

  const chooseAddressFromSearch = ({
    name,
    neighborhood,
    id,
    zipcode,
    search,
    results,
    cityId,
    cityName,
    stateId,
    stateAcronym,
    cityRef,
    type,
    neighborhoodId,
  }) => {
    const isWeakStreet = type === 'weakStreet'

    setAddress(prev => ({
      ...prev,
      weak: false,
      // O cityZipcode do Endereço Extra é o da cidade, não indica CEP único.
      cityZipcode: undefined,
      streetId: isWeakStreet ? undefined : id,
      weakStreetId: isWeakStreet ? id : undefined,
      selectedWeakNeighborhoodId: isWeakStreet
        ? neighborhoodId
        : prev.selectedWeakNeighborhoodId,
      street: name,
      zipcode,
      neighborhood,
      results,
      search,
      cityId,
      cityName,
      stateId,
      stateAcronym,
      cityRef,
    }))
  }

  const shouldDisplayForm = () =>
    !!address.streetId ||
    !!address.weakStreetId ||
    !!address.cityZipcode ||
    address.weak

  const canProceed = () => {
    if (address.weakStreetId) return true

    if (address.cityZipcode || address.weak) {
      return (
        validators.addressTextIsValid(address.street) &&
        validators.addressTextIsValid(address.neighborhood)
      )
    }

    return shouldDisplayForm()
  }

  const validateEnergyFields = () => {
    const { requestEnergyBillData, energyCodeType } = appService.getLocalPublicCall()

    if (!requestEnergyBillData) {
      setLocalErrors({})
      return true
    }

    const errs = {}

    if (
      address.servedByElectricity === null ||
      address.servedByElectricity === undefined
    ) {
      errs.servedByElectricity = ['Não pode ficar em branco']
    } else if (address.servedByElectricity === true) {
      if (!address.electricalPowerOperatorId) {
        errs.electricalPowerOperatorId = ['Não pode ficar em branco']
      }

      if (energyUtils.requiresEitherCode(energyCodeType)) {
        if (!address.consumerUnitNumber && !address.electricityInstallationCode) {
          errs.consumerUnitNumber = ['É obrigatório informar ao menos um dos campos']
          errs.electricityInstallationCode = ['É obrigatório informar ao menos um dos campos']
        }
      } else {
        if (
          energyUtils.showsConsumerUnitNumber(energyCodeType) &&
          !address.consumerUnitNumber
        ) {
          errs.consumerUnitNumber = ['Não pode ficar em branco']
        }
        if (
          energyUtils.showsInstallationCode(energyCodeType) &&
          !address.electricityInstallationCode
        ) {
          errs.electricityInstallationCode = ['Não pode ficar em branco']
        }
      }
    }

    setLocalErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleNext = () => {
    if (!validateEnergyFields()) return
    persist({ address })()
  }

  return (
    <>
      <StepHint>
        Os campos devem ser preenchidos com o endereço de residência do aluno
        candidato
      </StepHint>

      <Main>
        <Editing />

        <Search
          address={address}
          chooseAddressFromSearch={chooseAddressFromSearch}
          errors={filteredErrors}
          loading={loading}
          validating={validating}
          setLoading={setLoading}
          update={update}
        />

        {shouldDisplayForm() && (
          <>
            <Form
              address={address}
              errors={filteredErrors}
              loading={loading}
              setLoading={setLoading}
              validating={validating}
              update={update}
            />

            <EnergySection
              address={address}
              errors={{ ...localErrors, ...energyBackendErrors }}
              validating={validating}
              update={update}
            />
          </>
        )}
      </Main>

      <Buttons>
        <Button
          color="primary"
          disabled={validating}
          onClick={prev}
          variant="contained"
        >
          Voltar
        </Button>

        <LoadingButton
          disabled={!canProceed() || validating}
          loading={validating}
          onClick={handleNext}
        >
          Próxima
        </LoadingButton>
      </Buttons>
    </>
  )
}

export default Address
